import { Injectable, signal } from '@angular/core';

/**
 * Manages a queue of currently playing video IDs using Angular signals.
 *
 * Ensures uniqueness of video IDs and enforces a "one video per group" policy when using
 * the `groupName::videoId` format. When adding a video with a group prefix, any existing
 * video from the same group is automatically removed.
 *
 * @remarks
 * - The queue is implemented as a `Set` to guarantee uniqueness
 * - Group-based exclusivity: only one video per group can be active at a time
 * - The internal signal is exposed as readonly to prevent external mutations
 *
 * @example
 * ```
 * const videoService = inject(VideoService);
 *
 * // Add a video with group prefix
 * videoService.addVideoToQueue('trailer::video123');
 *
 * // Adding another video from the same group replaces the previous one
 * videoService.addVideoToQueue('trailer::video456'); // Removes 'trailer::video123'
 *
 * // Check current queue
 * const current = videoService.playingVideos();
 *
 * // Clean up
 * videoService.removeVideoFromQueue('trailer::video456');
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class VideoService {
  private readonly _playingVideos = signal(new Set<string>());
  playingVideos = this._playingVideos.asReadonly();

  /**
   * Clears all videos from the queue by resetting the internal signal.
   */
  destroy(): void {
    this._playingVideos.set(new Set());
  }

  /**
   * Adds a video ID to the queue with optional group-based exclusivity.
   *
   * If the `videoId` follows the `groupName::id` format, any existing video from the same
   * group is removed before adding the new one. This ensures only one video per group
   * can be active at a time.
   *
   * @param videoId - The video identifier, optionally prefixed with a group (e.g., `'trailer::video123'`)
   *
   * @remarks
   * - Empty or falsy `videoId` values are silently ignored
   * - Group detection is based on the presence of `::` separator
   * - Videos without group prefix can coexist with grouped videos
   *
   * @example
   * ```
   * videoService.addVideoToQueue('main::intro');
   * videoService.addVideoToQueue('main::outro'); // Replaces 'main::intro'
   * videoService.addVideoToQueue('standalone123'); // Added alongside grouped videos
   * ```
   */
  addVideoToQueue(videoId: string): void {
    if (!videoId) return;
    const [groupName] = videoId.split('::');

    this._playingVideos.update((set) => {
      const updatedSet = new Set(set);

      if (groupName) {
        const prefix = `${groupName}::`;

        for (const id of updatedSet) {
          if (id.startsWith(prefix)) {
            updatedSet.delete(id);
          }
        }
      }

      updatedSet.add(videoId);
      return updatedSet;
    });
  }

  /**
   * Removes a specific video ID from the queue.
   *
   * @param videoId - The video identifier to remove
   *
   * @remarks
   * No signal update occurs if the ID is not present in the queue, optimizing reactivity.
   */
  removeVideoFromQueue(videoId: string): void {
    if (!videoId) return;
    this._playingVideos.update((set) => {
      if (!set.has(videoId)) return set;
      const newSet = new Set(set);
      newSet.delete(videoId);
      return newSet;
    });
  }
}
